import { z } from 'zod';

export enum TabType {
  REGULAR = 0,
  POLICY = 1,
  INFOGRAPHIC = 2,
  VIDEO = 3,
  PDF = 4,
  FAQ = 5,
}

export const userTenantSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  policy_text: z.string(),
  logo: z.coerce.number(),
  logo_url: z.string(),
  banner: z.coerce.number(),
  banner_url: z.string(),
  slug: z.string(),
  is_username_and_password_login_enabled: z.boolean(),
  wb_program_url: z.string(),
  is_mfa_enabled: z.boolean(),
  hotline_number: z.string(),
  mailing_address: z.string(),
  auto_first_response_message: z.string(),
  first_response_deadline_days: z.coerce.number(),
  final_response_deadline_days: z.coerce.number(),
  is_auto_response_message_enabled: z.boolean(),
  banner_heading: z.string().nullable(),
  banner_subheading: z.string().nullable(),
  infographic: z.coerce.number(),
  infographic_url: z.string(),
  infographic_is_display_on_program_overview_enabled: z.boolean(),
  is_wb_meeting_allowed: z.boolean(),
  assigned_tenants: z.array(
    z.object({
      id: z.coerce.number(),
      name: z.string(),
      slug: z.string(),
    }),
  ),
  languages: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
    }),
  ),
  language_ids: z.array(z.number()),
  base_language: z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
  }),
  is_rectangular_logo_enabled: z.boolean(),
  bio: z.string().nullable(),
});
export type UserTenant = z.infer<typeof userTenantSchema>;

export const tenantUser = z.object({
  id: z.coerce.number(),
  full_name: z.string(),
  user_type: z.coerce.number(),
  department_name: z.string(),
  is_removed: z.boolean(),
  is_active: z.boolean(),
  is_invite_link_expired: z.boolean(),
  user_settings_invite_status: z.string(),
});
export type TentantUser = z.infer<typeof tenantUser>;

export const updateTenantFlagsSchema = z.object({
  is_schedule_allowed: z.boolean(),
  is_voice_report_allowed: z.boolean(),
  auto_response_message: z.string(),
  banner_heading: z.string(),
  banner_subheading: z.string(),
  out_of_scope_message: z.string(),
  final_response_deadline_days: z.number(),
  first_response_deadline_days: z.number(),
  show_out_of_scope_message: z.boolean(),
  is_auto_response_message_enabled: z.boolean(),
  key_points: z.array(z.string()),
  success_report_submit_message: z.string(),
});
export type UpdateTenantFlags = z.infer<typeof updateTenantFlagsSchema>;

export const departmentSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  type: z.coerce.number(),
  is_active: z.boolean(),
  classification: z.string(),
});
export type Department = z.infer<typeof departmentSchema>;

export const localizedDepartmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  translations: z.record(
    z.string(),
    z.object({
      main_id: z.number(),
      language_id: z.number(),
      id: z.number(),
      name: z.string(),
    }),
  ),
});
export type LocalizedDepartment = z.infer<typeof localizedDepartmentSchema>;

export type NewUserTypePayload = {
  email: string;
  user_type: number;
  department: number;
  full_name: string;
};

const REPORT_CLASSIFICATIONS = {
  Default: 'Default',
  Mandatory: 'Mandatory',
} as const;
export const classificationSchema = z.nativeEnum(REPORT_CLASSIFICATIONS);
export type Classification = z.infer<typeof classificationSchema>;

export const allowedReportTypeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  type: z.coerce.number(),
  is_active: z.boolean(),
  classification: classificationSchema,
  is_test: z.boolean(),
  is_auto_assignment_enabled: z.boolean(),
  auto_assignment_department: z.coerce.number().nullable(),
  auto_assignment_user: z.coerce.number().nullable(),
});
export type AllowedReportType = z.infer<typeof allowedReportTypeSchema>;

export const roleSettingsSchema = z.object({
  is_only_allowed_to_view_assigned_report: z.boolean(),
  is_only_allowed_to_view_report_in_department: z.boolean(),
  is_allowed_to_reopen_report: z.boolean(),
  is_allowed_to_request_pii_removal: z.boolean(),
  is_allowed_to_redact_report: z.boolean(),
  allowed_report_types: z.array(allowedReportTypeSchema),
  is_read_only_report: z.boolean(),
  is_allowed_to_send_messages: z.boolean(),
  is_allowed_to_generate_final_report: z.boolean(),
  is_allowed_to_request_for_additional_information: z.boolean(),
  is_allowed_to_create_final_report_template: z.boolean(),
  is_allowed_to_download_final_report: z.boolean(),
  is_allowed_to_download_attachments: z.boolean(),
  allowed_tenants: z.array(z.any()),
  user_settings_invite_status: z.string(),
  invite_link_hours_remaining: z.coerce.number(),
  is_invite_link_expired: z.boolean(),
  email: z.string(),
});
export type RoleSettings = z.infer<typeof roleSettingsSchema>;

export const updateRoleSettingsSchema = z.object({
  allowed_form_type_ids: z.array(z.number()),
});
export type RoleSettingsUpdate = z.infer<typeof updateRoleSettingsSchema>;

export const reportTypeSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  type: z.coerce.number(),
  is_active: z.boolean(),
  classification: z.string(),
  is_test: z.boolean(),
  is_auto_assignment_enabled: z.boolean(),
  auto_assignment_department: z.coerce.number(),
  auto_assignment_user: z.coerce.number(),
  is_non_whistleblowing: z.boolean(),
  risk_score: z.number(),
});
export type ReportType = z.infer<typeof reportTypeSchema>;

export const allowedIpAddressSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  ip_address: z.string(),
  is_active: z.boolean(),
});
export type AllowedIpAddress = z.infer<typeof allowedIpAddressSchema>;

export const tenantFlagsSchema = z.object({
  is_username_and_password_login_enabled: z.boolean(),
  is_mfa_enabled: z.boolean(),
  is_program_notifications_enabled: z.boolean(),
});
export type TenantFlags = z.infer<typeof tenantFlagsSchema>;

export const tenantNoteCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  created_at: z.string().datetime(),
});
export type TenantNoteCategory = z.infer<typeof tenantNoteCategorySchema>;

export const intakeChannelLogoSchema = z.object({
  logo_url: z.number(),
  banner_url: z.string(),
});
export type IntakeChannelLogo = z.infer<typeof intakeChannelLogoSchema>;

export const intakeChannelSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.number(),
});
export type IntakeChannel = z.infer<typeof intakeChannelSchema>;

export const customFieldSchema = z.object({
  id: z.number(),
  title: z.string(),
  dropdown_choices: z.array(z.string()).optional(),
  type: z.number(),
  deleted_at: z.boolean(),
  sequence: z.number(),
  allow_multi_value: z.boolean(),
  is_required: z.boolean(),
});
export type CustomField = z.infer<typeof customFieldSchema>;

export const UpdateReportTypeSchema = z.object({
  id: z.number(),
  name: z.string().optional(),
  type: z.number().optional(),
  is_active: z.boolean().optional(),
  classification: z.string().optional(),
  is_auto_assignment_enabled: z.boolean().optional(),
  auto_assignment_department: z.number().nullable().optional(),
  auto_assignment_user: z.number().nullable().optional(),
  is_non_whistleblowing: z.boolean().optional(),
});
export type UpdateReportType = z.infer<typeof UpdateReportTypeSchema>;

export const faqSchema = z.object({
  question: z.string().min(1),
  answer: z.string().min(1),
  id: z.number().optional(),
  order: z.number().optional(),
});
export type FAQObject = z.infer<typeof faqSchema>;

const baseProgramTabsSchema = z.object({
  id: z.number(),
  title: z.string(),
  is_display_on_program_overview_enabled: z.boolean(),
  is_deletable: z.boolean(),
  is_infographic: z.boolean(),
  sequence: z.number(),
});

const policyTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.POLICY),
  content: z.string().catch(''),
});
const regularTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.REGULAR),
  content: z.string().catch(''),
});
const faqTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.FAQ),
  content: z.array(faqSchema),
});
const videoTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.VIDEO),
  content: z.string().catch(''),
});
const pdfTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.PDF),
  content: z.string().catch(''),
});
const infographicTabSchema = baseProgramTabsSchema.extend({
  tab_type: z.literal(TabType.INFOGRAPHIC),
  content: z.string().catch(''),
});

export const programTabSchema = z.discriminatedUnion('tab_type', [
  policyTabSchema,
  regularTabSchema,
  faqTabSchema,
  videoTabSchema,
  pdfTabSchema,
  infographicTabSchema,
]);

export type ProgramTab = z.infer<typeof programTabSchema>;

export const IntakeChannelDetailsSchema = z.object({
  id: z.number(),
  name: z.string(),
  is_schedule_allowed: z.boolean(),
  is_voice_report_allowed: z.boolean(),
  report_types: z.array(reportTypeSchema),
  program_tabs: z.array(programTabSchema),
  custom_fields: z.array(customFieldSchema),
  auto_response_message: z.string().nullable(),
  banner_heading: z.string().nullable(),
  banner_subheading: z.string().nullable(),
  out_of_scope_message: z.string().nullable(),
  final_response_deadline_days: z.number(),
  first_response_deadline_days: z.number(),
  show_out_of_scope_message: z.boolean(),
  is_auto_response_message_enabled: z.boolean(),
  key_points: z.array(z.string()).nullable(),
  success_report_submit_message: z.string().nullable(),
});

export type IntakeChannelDetails = z.infer<typeof IntakeChannelDetailsSchema>;

export const updateReportFormSchema = z.object({
  active_countries: z
    .array(z.object({ code: z.string(), name: z.string() }))
    .optional(),
  inactive_countries: z
    .array(z.object({ code: z.string(), name: z.string() }))
    .optional(),
  is_anonymous_reporting_enabled: z.boolean().optional(),
  delete_report_custom_field_ids: z.number().optional(),
  delete_report_type_ids: z.array(z.number()).optional(),
  report_custom_fields: z
    .object({
      id: z.number(),
      name: z.string(),
      dropdown_choices: z.array(z.string()).optional(),
    })
    .optional(),
  report_types: z.array(UpdateReportTypeSchema).optional(),
});
export type UpdateReportForm = z.infer<typeof updateReportFormSchema>;

export const updateTenantSchema = z.object({
  hotline_number: z.string().optional(),
  mailingAddress: z.string().optional(),
  logo_id: z.number().optional(),
  policy_text: z.string().optional(),
  infographic: z.number().optional(),
});
export type UpdateTenant = z.infer<typeof updateTenantSchema>;

export const languageSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
});

export const tenantProgramSchema = z.object({
  id: z.number(),
  name: z.string(),
  policy_text: z.string(),
  logo: z.number(),
  slug: z.string(),
  is_username_and_password_login_enabled: z.boolean(),
  is_mfa_enabled: z.boolean(),
  is_program_notifications_enabled: z.boolean(),
  base_language: languageSchema,
  languages: z.array(languageSchema),
  language_ids: z.array(z.number()),

  program_tabs: z.array(programTabSchema),
  infographic: z.number().optional(),
  infographic_is_display_on_program_overview_enabled: z.boolean(),
  infographic_url: z.string(),
  is_wb_meeting_allowed: z.boolean(),
  wb_program_url: z.string(),
  logo_url: z.string(),
  hotline_number: z.string(),
  mailing_address: z.string(),
  auto_first_response_message: z.string(),
  first_response_deadline_days: z.number(),
  final_response_deadline_days: z.number(),
  is_auto_response_message_enabled: z.boolean(),
  banner_heading: z.string(),
  banner_subheading: z.string(),
  is_rectangular_logo_enabled: z.boolean(),
});

export type TenantProgram = z.infer<typeof tenantProgramSchema>;

export const baseTabPayloadSchema = z.object({
  tab_type: z.nativeEnum(TabType),
  title: z.string().optional(),
  content: z.union([
    z.string().trim().optional(),
    z.array(faqSchema).optional(),
  ]),
  file_type: z.string().optional(),
  is_display_on_program_overview_enabled: z.boolean(),
});

export const tabPayload = baseTabPayloadSchema.extend({
  id: z.number().optional(),
});

export type NewTabPayload = z.infer<typeof baseTabPayloadSchema>;
export type TabPayload = z.infer<typeof tabPayload>;

export const addCommentPolicySchema = z.object({
  content: z.string(),
  highlighted_text: z.string(),
  tagged_users: z.array(z.number()),
});
export type AddCommentPolicy = z.infer<typeof addCommentPolicySchema>;

export const customErrorTypesSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
});
export type CustomErrorTypes = z.infer<typeof customErrorTypesSchema>;

export const submitCustomerTicketSchema = z.object({
  subject: z.string(),
  description: z.string(),
  error_type: z.string(),
  page: z.string(),
  files: z.array(
    z.object({
      base64: z.string(),
      fileName: z.string(),
      fileType: z.string(),
    }),
  ),
});
export type SubmitCustomerTicket = z.infer<typeof submitCustomerTicketSchema>;

export const channelCountriesschema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  is_active: z.boolean(),
  risk_score: z.number(),
});

export type ChannelCountries = z.infer<typeof channelCountriesschema>;

export const reportFormDataSchema = z.object({
  is_anonymous_reporting_enabled: z.boolean(),
  is_show_non_whistleblowing_message: z.boolean(),
  non_whistleblowing_message: z.string(),
  active_countries: z
    .array(z.object({ code: z.string(), name: z.string() }))
    .optional(),
  inactive_countries: z
    .array(z.object({ code: z.string(), name: z.string() }))
    .optional(),
  report_custom_fields: z
    .object({
      id: z.number(),
      title: z.string(),
      type: z.number(),
      dropdown_choices: z.array(z.string()),
      deleted_at: z.null(),
      allow_multi_value: z.boolean(),
      is_required: z.boolean(),
      sequence: z.number(),
      report_field_name: z.string(),
    })
    .optional(),
  report_types: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      type: z.number(),
      is_active: z.boolean(),
      classification: z.string(),
      is_auto_assignment_enabled: z.boolean(),
      auto_assignment_department: z.number().nullable(),
      auto_assignment_user: z.number().nullable(),
      is_non_whistleblowing: z.boolean(),
      risk_score: z.number(),
    }),
  ),
  tenant_users: z.array(
    z.object({
      full_name: z.string().nullable(),
      id: z.coerce.number(),
    }),
  ),
  departments: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      type: z.number(),
      is_active: z.boolean(),
      classification: z.string(),
    }),
  ),
});

export type ReportFormData = z.infer<typeof reportFormDataSchema>;
export const intakeChannelProgramLogoSchema = z.object({
  banner_url: z.string(),
  logo_url: z.string(),
});
export type IntakeChannelProgramLogo = z.infer<
  typeof intakeChannelProgramLogoSchema
>;

export const intakeChannelProgramDetailsSchema = z.object({
  id: z.number(),
  name: z.string(),
  report_types: z.array(reportTypeSchema),
  program_tabs: z.array(programTabSchema),
  custom_fields: z.array(customFieldSchema),
  key_points: z.array(z.string()).nullable(),
});
export type IntakeChannelProgramDetails = z.infer<
  typeof intakeChannelProgramDetailsSchema
>;

export const replyResponseSchema = z.object({
  id: z.number(),
  content: z.string(),
  is_edited: z.boolean(),
  updated_at: z.string(),
  author_details: z.object({
    id: z.number(),
    full_name: z.string(),
  }),
  has_seen_users: z.array(z.number()),
  tagged_users_details: z.array(
    z.object({
      id: z.number(),
      full_name: z.string(),
    }),
  ),
});

export const commentResponseSchema = z.object({
  id: z.number(),
  content: z.string(),
  highlighted_text: z.string(),
  is_resolved: z.boolean(),
  is_edited: z.boolean(),
  updated_at: z.string(),
  author_details: z.object({
    id: z.number(),
    full_name: z.string(),
  }),
  has_seen_users: z.array(z.number()),
  tagged_users_details: z.array(
    z.object({
      id: z.number(),
      full_name: z.string(),
    }),
  ),
  replies: z.array(replyResponseSchema),
});
export type CommentResponse = z.infer<typeof commentResponseSchema>;
